import { PIPELINE_MIGRATE as MODELS_PIPELINE_MIGRATE } from '../../content/productModelsPage';
import { SHOT_CONTROL_FAQ } from '../../content/productShotControlPage';
import { PageFaqSection } from '../ds';
import { CDN } from '../../lib/cdn';
import { ProductFinalCta } from './ProductFinalCta';
import { ProductHeroHeader } from './ProductHeroHeader';
import { ProductMigrate } from './ProductMigrate';

export function ProductShotControlSections() {
  return (
    <div className="product-page">
      <ProductHeroHeader
        label="Product - Shot Control"
        title={
          <>
            Director-Grade
            <br />
            <em>Shot Control</em>
          </>
        }
        sub="Override any shot — angle, lens, lighting, pacing, performance — without touching the cut around it. Local, non-destructive, re-verified before it lands."
        note="Local override · Non-destructive · Re-verified · Plain-language notes"
        still={{
          kind: 'camera_abstract',
          slug: 'DIRECTOR / OVERRIDE',
          scene: 'CONTROL / 01',
          genre: 'DIRECTION',
          sid: 'shot-hero',
          alt: 'Director-grade shot control in Induce - overriding angle, lens, and lighting on a single shot',
          videoSrc: CDN.videos.shotControl,
        }}
      />

      <section className="answer band-light" aria-labelledby="sc-aeo-h" id="the-problem">
        <div className="container">
          <div className="content-measure">
            <div className="tag">- The problem</div>
            <div className="answer-body">
              <h2 id="sc-aeo-h" className="a-lead">
                <b>Fix one shot without re-rolling the film.</b>
              </h2>
              <p className="a-body">
                {/* In most AI video, fixing one shot means re-rolling everything and hoping the rest survives. */}
                In Induce, every override — angle, lens, lighting, pacing, performance — is local, so the
                surrounding cut stays untouched. And when a shot is wrong, Rhapsody names the cause: the
                continuity link, film-grammar rule, or story beat that broke.
              </p>
              <div className="a-stats reveal">
                <div className="a-stat">
                  <b>1</b>
                  <span>Shot re-generated per override</span>
                </div>
                <div className="a-stat">
                  <b>0</b>
                  <span>Surrounding shots affected</span>
                </div>
                <div className="a-stat">
                  <b>2</b>
                  <span>Verification axes on every override</span>
                </div>
                <div className="a-stat">
                  <b>Plain</b>
                  <span>Language notes accepted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="band-dark" id="override">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- What you can control</span>
            <h2>
              Six controls. Any shot. <em>No re-roll.</em>
            </h2>
          </div>
          <div className="step-flow reveal">
            {[
              [
                '01',
                'Angle & framing',
                'Recompose a shot without touching its neighbors.',
                'Framing',
              ],
              ['02', 'Lens & optics', 'Change focal length and depth for a specific moment.', 'Optics'],
              ['03', 'Lighting', 'Relight a single shot to hit the beat you want.', 'Light'],
              ['04', 'Pacing', 'Adjust the timing and rhythm of a cut.', 'Time'],
              ['05', 'Performance', 'Shift the emotional read of a shot.', 'Performance'],
              ['06', 'Blocking', 'Reposition how subjects move and sit within the frame.', 'Blocking'],
            ].map(([n, title, body, tag]) => (
              <div key={n} className="step-item">
                <div className="step-n">{n}</div>
                <div className="step-body">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className="step-tag">{tag}</div>
              </div>
            ))}
          </div>
          <div className="highlight-box reveal section-stack">
            <div className="h-label">- Control that respects continuity</div>
            <p>
              Every override runs on the Project Memory Graph and back through the Coherence Verifier, so it
              can&apos;t silently break character, location, or film grammar. Re-angle a shot and the
              protagonist stays the protagonist. Cross the line, and Induce tells you before your audience
              does.
            </p>
          </div>
          <div className="sec-head reveal section-stack">
            <span className="label-mono">- Cinematic control over shots</span>
            <h2>Built for teams that obsess over details.</h2>
            <p className="sub">
              For directors who won&apos;t accept a generic generation, cinematographers dialing in coverage
              and look, and agencies reworking a hero shot without rebuilding the film. Rhapsody plans,
              verifies, and flags — but nothing ships without your sign-off.
            </p>
          </div>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Shot control, answered"
        title={
          <>
            Shot control, <em>answered.</em>
          </>
        }
        items={SHOT_CONTROL_FAQ}
      />

      <ProductMigrate {...MODELS_PIPELINE_MIGRATE} tone="dark" />

      <ProductFinalCta
        tone="light"
        eyebrow="- Direct every shot"
        title={
          <>
            Direct every shot. Keep the whole <em>cut.</em>
          </>
        }
        body="Override any shot without breaking continuity, film grammar, or the surrounding timeline."
        ghostLabel="Talk to Sales"
        ghostIsSales
        note="Free beta · No credit card · Commercial rights included"
      />
    </div>
  );
}
